import type {
  ImportAdapter,
  ImportStepConfig,
  ImportWizardState,
} from '../types';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if file select step is valid
 */
export function isFileSelectValid(state: ImportWizardState): boolean {
  if (state.fileData.useCopyPaste) {
    return state.fileData.pastedText.trim().length > 0;
  }
  return state.fileData.file !== null;
}

/**
 * Check if column mapping step is valid
 * For contacts: requires at least one identifier (email, phone, or all 3 push fields)
 * For custom data: requires all required fields to be mapped
 */
export function isColumnMappingValid(state: ImportWizardState): boolean {
  return state.mapping.length > 0;
}

/**
 * Check if contact mapping has at least one valid identifier
 */
export function hasContactIdentifier(state: ImportWizardState): boolean {
  const mappedTargets = new Set(state.mapping.map((m) => m.target));

  // Email or phone is sufficient
  if (mappedTargets.has('email') || mappedTargets.has('phone')) {
    return true;
  }

  // Push notification requires all 3 fields
  if (mappedTargets.has('push_endpoint')
      && mappedTargets.has('push_key')
      && mappedTargets.has('push_token')) {
    return true;
  }

  return false;
}

/**
 * Check if item selector step is valid
 * Counts only items with selected: true
 */
export function isItemSelectorValid(
  state: ImportWizardState,
  minSelection = 1,
): boolean {
  const selectedCount = state.selectedItems.filter((item) => item.selected).length;
  return selectedCount >= minSelection;
}

/**
 * Get file type from file name
 */
export function getFileTypeFromName(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'csv':
      return 'csv';
    case 'xlsx':
    case 'xls':
      return 'xlsx';
    case 'txt':
      return 'txt';
    case 'json':
      return 'json';
    case 'zip':
      return 'zip';
    default:
      return 'csv';
  }
}

/**
 * Check if adapter has a specific step type
 */
export function hasStepType(adapter: ImportAdapter, type: string): boolean {
  return adapter.steps.some((step) => step.type === type);
}

/**
 * Get step index by type
 */
export function getStepIndexByType(adapter: ImportAdapter, type: string): number {
  return adapter.steps.findIndex((step) => step.type === type);
}

// =============================================================================
// DEFAULT CAN PROCEED IMPLEMENTATION
// =============================================================================

/**
 * Default implementation for canProceed
 * Can be used by adapters that don't need custom logic
 */
export function defaultCanProceed(
  adapter: ImportAdapter,
  stepIndex: number,
  state: ImportWizardState,
): boolean {
  const step = adapter.steps[stepIndex];
  if (!step) return false;

  // Use custom validation if provided
  if (step.validate) {
    return step.validate(state);
  }

  // Default validation based on step type
  switch (step.type) {
    case 'file_select':
      return isFileSelectValid(state);
    case 'column_mapping':
      return isColumnMappingValid(state);
    case 'item_selector': {
      const config = adapter.getItemSelectorConfig?.();
      const minSelection = config?.minSelection ?? 1;
      return isItemSelectorValid(state, minSelection);
    }
    case 'preview':
      return true; // Preview step is always valid
    default:
      return false;
  }
}

// =============================================================================
// ADAPTER FACTORY HELPER
// =============================================================================

/**
 * Adapter configuration without canProceed (it will be added automatically)
 */
type AdapterConfig = Omit<ImportAdapter, 'canProceed'> & {
  canProceed?: (stepIndex: number, state: ImportWizardState) => boolean;
};

/**
 * Create an adapter with default canProceed implementation
 */
export function createAdapter(config: AdapterConfig): ImportAdapter {
  const adapter: ImportAdapter = {
    ...config,
    canProceed: config.canProceed
      ?? ((stepIndex: number, state: ImportWizardState) => defaultCanProceed(
        adapter,
        stepIndex,
        state,
      )),
  };
  return adapter;
}

/**
 * Helper to create step configurations
 */
export function createSteps(steps: ImportStepConfig[]): ImportStepConfig[] {
  return steps;
}
